import { useCallback } from "react";
import { Platform } from "react-native";

import { useAppVersion } from "@helpers/app-version";
import i18n from "@helpers/lib/i18n";
import { trackCustomEvent } from "analytics";
import useSessionStore from "./useSessionStore";

import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useAppStore } from "@hooks/useAppStore";
import type { FeedbackParams } from "@memorang/features/src/feedback/types";
import { useAilaStore } from "./useAilaStore";

export const useQASearch = () => {
	const { email } = useAuthContext();
	const appVersion = useAppVersion();

	const tenantName = useAppStore((store) => store.tenant.name);
	const itemId = useAilaStore((state) => state.itemId);
	const sessionId = useSessionStore((state) => state.id);
	const inReviewMode = useSessionStore((state) => state.inReviewMode);
	const isSummativeTest = useSessionStore((state) => state.isSummativeTest);

	const {
		currentExamContext: {
			projectId,
			organizationId,
			studyPackProductNumericId,
		},
	} = useExamContext();

	const {
		currentExamContext: {
			studyPackCollectionId,
			practiceTestProductNumericId,
			examName,
			examId,
		},
	} = useExamContext();

	const handleTracking = useCallback(
		(eventName: string, data?: Record<string, string>) => {
			const viewForFormative = inReviewMode
				? "review_session_formative"
				: "session_formative";
			trackCustomEvent({
				eventName,
				...(data || {}),
				view: isSummativeTest ? "review_session_summative" : viewForFormative,
			});
		},
		[inReviewMode, isSummativeTest],
	);

	const type = practiceTestProductNumericId ? "Practice Test" : "Study-Pack";
	const contextId = practiceTestProductNumericId ?? studyPackCollectionId;

	const feedbackDialogParams: FeedbackParams = {
		email: email!,
		tenantId: tenantName,
		sessionId: `${sessionId}`,
		appLanguage: i18n.language,
		platform: Platform.OS.toUpperCase(),
		currentAppVersion: appVersion,
		view: isSummativeTest
			? "Summative Review Session"
			: "Formative Review Session",
		context: examName,
		product: `${examName} ${type}`,
		feature: "AI QA",
		projectId,
		organizationId,
		productId: studyPackProductNumericId!,
		itemId,
		itemVersionId: "1",
		sessionType: "FORMATIVE",
	};

	return {
		examId,
		contextId,
		handleTracking,
		feedbackDialogParams,
		examName,
	};
};
